import { Preferences } from '@capacitor/preferences'

import { isNativeApp } from '@/utils/platform'

/** The key the calling feature first stored it under, so phones that already have an id keep it. */
const NATIVE_KEY = 'calling.installationId'
const WEB_KEY = 'push.installationId'

function newId(): string {
  return crypto.randomUUID()
}

/**
 * Random id that names this install (Android app) or browser profile to the server. Created once.
 * Several callers ask for it at the same moment on first launch; they share one lookup, otherwise
 * each would create its own id and register the phone as several devices (and get every push twice).
 */
export function installationId(): Promise<string> {
  if (isNativeApp()) {
    return (nativeId ??= readOrCreateNativeId().catch((error) => {
      nativeId = undefined // let a later call try again
      throw error
    }))
  }
  return Promise.resolve(webInstallationId())
}

let nativeId: Promise<string> | undefined

async function readOrCreateNativeId(): Promise<string> {
  const { value } = await Preferences.get({ key: NATIVE_KEY })
  if (value) return value
  const id = newId()
  await Preferences.set({ key: NATIVE_KEY, value: id })
  return id
}

function webInstallationId(): string {
  try {
    const existing = localStorage.getItem(WEB_KEY)
    if (existing) return existing
    const id = newId()
    localStorage.setItem(WEB_KEY, id)
    return id
  } catch {
    // Storage blocked: an id that lasts for this page load still lets the device register.
    return (sessionFallback ??= newId())
  }
}

let sessionFallback: string | undefined
